import {
	ioJsonAction,
	ioJsonDeleteItemAction,
	ioJsonFetchAction,
	ioJsonFetchItemAction, ioJsonManualAction,
	ioJsonSaveAction
} from "Core/store/actions/io";
import {hideLoading, hideLoadingFunction, showLoading} from "Core/helpers/loading";
import {getArray, isset} from "Core/helpers/data";
import {get} from "lodash";
import {actionCreators} from "Core/store/reducers";
import * as listItemDataMap from "./dataMap/listItem";
import * as itemFilterDataMap from "./dataMap/filter";
import * as itemDataMap from "./dataMap/item";
import {reducerStoreKey} from "./reducer";
import {isSuccessful} from "Core/helpers/io";

/**
 * Reset customers list Redux state to initial state
 * @return {(function(*))|*}
 */
export const resetCustomersListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].reset());
};

/**
 * Fetch customers list
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} projectId - ID of the project (customers are assigned to projects).
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchCustomersListAction = (
	abortCallback, projectId, filter = null, pageNo = 1, perPage, sortBy, sortDir
) => dispatch => {
	let filterToUse = filter || {};
	filterToUse = {...filterToUse, projectIds: [projectId]};
	
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		'customer/search',
		'',
		filterToUse,
		null,
		pageNo,
		perPage,
		sortBy,
		sortDir
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: itemFilterDataMap.input(get(responseData, 'filter')),
					data: getArray(responseData, 'data').map(i => listItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load customers list into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} projectId - ID of the project (customers are assigned to projects).
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay will be rendered.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadCustomersListAction = (
	abortCallback, projectId, filter = null, pageNo = 1, perPage, sortBy, sortDir, loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#main-page-table") : null);
	return fetchCustomersListAction(abortCallback, projectId, filter, pageNo, perPage, sortBy, sortDir)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setCustomersListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Set customer list filter in Redux store
 *
 * @param {Object} filter - Customer list filter to set.
 * @return {(function(*): void)|*}
 */
export const setCustomersListFilterAction = filter => dispatch => {
	dispatch(actionCreators[reducerStoreKey].setCustomersListFilter(filter));
};

/**
 * Clear customers list form Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearCustomersListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearCustomersListData());
}

/**
 * Fetch customers item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string|number} id - DB ID of the customers item to fetch.
 * @return {function(*=): Promise<CustomersItemDataObject>}
 */
export const fetchCustomersItemAction = (abortCallback, id) => dispatch => {
	return ioJsonFetchItemAction(
		abortCallback,
		'defaultAuthorizedApi',
		'customer/fetch-by-id',
		id,
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => itemDataMap.input(responseData?.data));
};

/**
 * Load customers item into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} id - DB ID of the customers item to load.
 * @return {function(*=): Promise<CustomersItemDataObject>}
 */
export const loadCustomersItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#item-popup');
	return fetchCustomersItemAction(abortCallback, id)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setCustomersItem(responseData));
			hideLoading(loading);
			return responseData;
		});
}

/**
 * Clear customers item from Redux store
 * @return {(function(*=): void)|*}
 */
export const clearCustomersItemAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearCustomersItem());
};

/**
 * Create customers item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} projectId - ID of the project (customers are assigned to projects).
 * @param {CustomersItemDataObject} item - Customers item to save.
 * @return {function(*=): Promise<CustomersItemDataObject>} Promise that will resolve with the created item 
 * received from IO or undefined if creation failed.
 */
export const createCustomersItemAction = (abortCallback, projectId, item) => dispatch => {
	const loading = showLoading('#item-popup');
	return ioJsonSaveAction(
		// @note abortCallback is set to undefined because save actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'customer/create',
		{
			id: '',
			data: {
				...itemDataMap.output(item),
				projectIds: [projectId],
			}
		},
		undefined,
		true,
		hideLoadingFunction(loading)
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => isset(responseData) ? itemDataMap.input(responseData.data) : undefined);
};

/**
 * Update customers item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} projectId - ID of the project (customers are assigned to projects).
 * @param {string} id - DB ID of the customers item to update.
 * @param {CustomersItemDataObject} item - Customers item to save.
 * @return {function(*=): Promise<CustomersItemDataObject>} Promise that will resolve with the updated item 
 * received from IO or undefined if updating failed.
 */
export const updateCustomersItemAction = (abortCallback, projectId, id, item) => dispatch => {
	const loading = showLoading('#item-popup');
	
	return ioJsonSaveAction(
		// @note abortCallback is set to undefined because save actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'customer/update-by-id',
		{
			id,
			data: {
				...itemDataMap.output(item),
				projectIds: [projectId],
			}
		},
		undefined,
		true,
		hideLoadingFunction(loading)
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => isset(responseData) ? itemDataMap.input(responseData.data) : undefined)
		// Set current item to Redux store so that changes will be detected once item has been updated
		.then(updatedItem => {
			if (isset(updatedItem)) dispatch(actionCreators[reducerStoreKey].setCustomersItem(item));
			return updatedItem;
		})
		// Save the updated item to Redux store received from response (since 'requestSavedData' is set to true)
		.then(updatedItem => { 
			if (isset(updatedItem)) dispatch(actionCreators[reducerStoreKey].setCustomersItem(updatedItem));
			return updatedItem; 
		});
};

/**
 * Delete customers item action
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string|number} id - ID of the customers item that will be deleted.
 * @return {function(*): Promise<IoJsonFetchResponseObject>}
 */
export const deleteCustomersItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#item-delete-dialog .dialog-content-component .buttons');
	return ioJsonDeleteItemAction(
		// @note abortCallback is set to undefined because delete actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'', // TODO: Add endpoint when and if deleting users becomes available (don't forget to uncomment delete buttons)
		id,
		{},
		hideLoadingFunction(loading)
	)(dispatch);
};

/**
 * Check if customer are being imported from a file
 * @note Importing customers is done asynchronously on backend.
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {boolean} [ignoreErrors=false] - Flag that specifies if response errors will be ignored.
 * @param {?string} [clientId=null] - Client ID.
 * @return {function(*): Promise<boolean>}
 */
export const areCustomersBeingImportedFromFileAction = (
	abortCallback, ignoreErrors = false, clientId = null
) => dispatch => {
	const action = (ignoreErrors ? ioJsonManualAction : ioJsonAction);
	return action(
		abortCallback,
		'defaultAuthorizedApi',
		'customer/fetch-import-customer-requests',
		{clientId}
	)(dispatch)
		.then(res => isSuccessful(res) ? (getArray(res, 'data').length > 0) : undefined)
		.catch(() => { /* Do nothing */ });
};

/**
 * Fetch customer custom fields
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} [loadingOverlaySelector] - Selector for the loading overlay.
 * @param {?string} [clientId=null] - DB ID of the client (used by super admin).
 * @return {function(*): Promise<
 * 	{id: string, name: string, code: string, description: string, messageType: CampaignMessageContentType}[]|undefined
 * >}
 */
export const fetchCustomerCustomFieldsAction = (abortCallback, loadingOverlaySelector, clientId = null) => dispatch => {
	const loading = (loadingOverlaySelector ? showLoading(loadingOverlaySelector) : undefined);
	return ioJsonAction(
		abortCallback,
		'defaultAuthorizedApi',
		'customer/fetch-customer-custom-fields',
		{clientId},
		loading ? hideLoadingFunction(loading) : undefined,
	)(dispatch)
		// Get customer custom fields from response data
		.then(res => (res ? getArray(res, 'data') : undefined));
}